import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import {
  SportsFootball as SportsFootballIcon,
  Group as GroupIcon,
  EmojiEvents as TrophyIcon,
  Description as DescriptionIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  BarChart as StatsIcon,
  Forum as ForumIcon
} from '@mui/icons-material';

const GettingStarted = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const steps = [
    {
      icon: <DescriptionIcon color="primary" />,
      title: '1. Learn the Basics',
      description: 'Understand how contract-based fantasy football works and the unique features of CAPSized.',
      action: 'Read Guide',
      onClick: () => {}
    },
    {
      icon: <PersonAddIcon color="primary" />,
      title: '2. Join or Create a League',
      description: 'Start by joining an existing league or create your own with custom settings.',
      action: 'Create League',
      onClick: () => navigate('/createLeague')
    },
    {
      icon: <EditIcon color="primary" />,
      title: '3. Set Up Your Team',
      description: 'Draft players, manage your roster, and set your lineup for the upcoming games.',
      action: 'View Team Setup',
      onClick: () => {}
    },
    {
      icon: <StatsIcon color="primary" />,
      title: '4. Track Performance',
      description: 'Monitor your team\'s performance, player stats, and league standings.',
      action: 'View Stats',
      onClick: () => {}
    },
    {
      icon: <ForumIcon color="primary" />,
      title: '5. Engage with the Community',
      description: 'Connect with other managers, discuss strategies, and participate in league activities.',
      action: 'Join Community',
      onClick: () => {}
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Hero Section */}
      <Box textAlign="center" mb={6}>
        <Typography 
          variant={isMobile ? 'h4' : 'h3'} 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            mb: 2
          }}
        >
          Getting Started with CAPSized
        </Typography>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          color="text.secondary" 
          paragraph
          sx={{ maxWidth: '800px', mx: 'auto' }}
        >
          Everything you need to know to start your contract-based fantasy football journey
        </Typography>
      </Box>

      {/* Getting Started Steps */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 4 },
          mb: 6,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 4, fontWeight: 'medium' }}>
          Quick Start Guide
        </Typography>
        
        <List sx={{ width: '100%' }}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <ListItem 
                alignItems="flex-start"
                sx={{ 
                  p: 3,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1
                  },
                  transition: 'background-color 0.2s',
                  cursor: 'pointer'
                }}
                onClick={step.onClick}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {step.icon}
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" component="div" gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {step.description}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      step.onClick();
                    }}
                  >
                    {step.action}
                  </Button>
                </Box>
              </ListItem>
              {index < steps.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Additional Resources */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 'medium' }}>
          Additional Resources
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" component="h3" gutterBottom>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                  Help Center
                </Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Find answers to common questions and learn more about CAPSized features.
              </Typography>
              <Button variant="text" color="primary">
                Visit Help Center
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" component="h3" gutterBottom>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ForumIcon color="primary" sx={{ mr: 1 }} />
                  Community Forums
                </Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Connect with other players, share strategies, and get tips from experienced managers.
              </Typography>
              <Button variant="text" color="primary">
                Join the Discussion
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Call to Action */}
      <Box textAlign="center" mt={8}>
        <Typography variant="h5" component="h2" gutterBottom>
          Ready to get started?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: '600px', mx: 'auto', mb: 3 }}>
          Create your first league or join an existing one to begin your CAPSized fantasy football experience.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          onClick={() => navigate('/createLeague')}
          startIcon={<GroupIcon />}
          sx={{ mr: 2, mb: 2 }}
        >
          Create a League
        </Button>
        <Button 
          variant="outlined" 
          color="primary" 
          size="large"
          onClick={() => {}}
          startIcon={<SportsFootballIcon />}
          sx={{ mb: 2 }}
        >
          Join a League
        </Button>
      </Box>
    </Container>
  );
};

export default GettingStarted;
